import React from "react";
import { useState, useEffect, useMemo } from "react";
import { Lock, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Combobox } from "@/components/ui/combobox";
import { useCachedPixels } from "@/hooks/useCachedPixels";
import { useCachedApps } from "@/hooks/useCachedApps";
import type { AdAccountSettings } from "@/hooks/useOnboardingSettings";

interface StepTrackingProps {
  adAccountId: string;
  settings: AdAccountSettings | null;
  onSave: (data: Partial<AdAccountSettings>) => Promise<any>;
  onValidChange: (valid: boolean) => void;
}

function LockedSwitch({
  label,
  checked,
  locked,
  lockReason,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  locked: boolean;
  lockReason?: string;
  onCheckedChange: (val: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex items-center gap-2">
        <span className="text-sm">{label}</span>
        {locked && lockReason && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent side="right">
              <p className="text-xs">{lockReason}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={locked} />
    </div>
  );
}

export function StepTracking({
  adAccountId,
  settings,
  onSave,
  onValidChange,
}: StepTrackingProps) {
  const pixelQuery = useCachedPixels(adAccountId, {
    refetchInterval: (query: any) => (query.state.data?.length ? false : 2000),
  });
  const appQuery = useCachedApps(adAccountId, {
    refetchInterval: (query: any) => (query.state.data?.length ? false : 2000),
  });
  const pixels = pixelQuery.data ?? [];
  const pixelsLoading = pixelQuery.isLoading;
  const apps = appQuery.data ?? [];

  const [pixelId, setPixelId] = useState<string>(settings?.default_pixel_id ?? "");
  const [appId, setAppId] = useState<string>(settings?.default_app_id ?? "");
  const [websiteEvents, setWebsiteEvents] = useState(settings?.website_events_enabled ?? false);
  const [appEvents, setAppEvents] = useState(settings?.app_events_enabled ?? false);
  const [offlineEvents, setOfflineEvents] = useState(settings?.offline_events_enabled ?? false);
  const [crmEvents, setCrmEvents] = useState(settings?.crm_events_enabled ?? false);
  const [postEngagements, setPostEngagements] = useState(settings?.post_engagements_enabled ?? false);
  const [pageEngagements, setPageEngagements] = useState(settings?.page_engagements_enabled ?? false);
  const [utmParameters, setUtmParameters] = useState(settings?.default_utm_parameters ?? "");

  // Sync from loaded settings
  useEffect(() => {
    if (settings) {
      if (settings.default_pixel_id) setPixelId(settings.default_pixel_id);
      if (settings.default_app_id) setAppId(settings.default_app_id);
      setWebsiteEvents(settings.website_events_enabled ?? false);
      setAppEvents(settings.app_events_enabled ?? false);
      setOfflineEvents(settings.offline_events_enabled ?? false);
      setCrmEvents(settings.crm_events_enabled ?? false);
      setPostEngagements(settings.post_engagements_enabled ?? false);
      setPageEngagements(settings.page_engagements_enabled ?? false);
      setUtmParameters(settings.default_utm_parameters ?? "");
    }
  }, [settings]);

  // Auto-enable website events when pixel is selected
  useEffect(() => {
    if (pixelId && !websiteEvents) setWebsiteEvents(true);
  }, [pixelId]);

  // Auto-enable app events when app is selected
  useEffect(() => {
    if (appId && !appEvents) setAppEvents(true);
  }, [appId]);

  const pixelOptions = useMemo(
    () => pixels.map((p) => ({ value: p.pixel_id, label: p.pixel_name, subtitle: p.pixel_id })),
    [pixels]
  );

  const appOptions = useMemo(
    () => apps.map((a) => ({ value: a.app_id, label: a.app_name, subtitle: a.app_id })),
    [apps]
  );

  // Always valid (all fields optional)
  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  // Auto-save on changes
  useEffect(() => {
    const timeout = setTimeout(async () => {
      const pixel = pixels.find((p) => p.pixel_id === pixelId);
      const app = apps.find((a) => a.app_id === appId);
      await onSave({
        default_pixel_id: pixelId || null,
        default_pixel_name: pixel?.pixel_name ?? null,
        default_app_id: appId || null,
        default_app_name: app?.app_name ?? null,
        website_events_enabled: websiteEvents,
        app_events_enabled: appEvents,
        offline_events_enabled: offlineEvents,
        crm_events_enabled: crmEvents,
        post_engagements_enabled: postEngagements,
        page_engagements_enabled: pageEngagements,
        default_utm_parameters: utmParameters,
        step_tracking_completed: true,
      });
    }, 500);
    return () => clearTimeout(timeout);
  }, [pixelId, appId, websiteEvents, appEvents, offlineEvents, crmEvents, postEngagements, pageEngagements, utmParameters]);

  // Syncing state for up to 5 seconds, then show "No pixels found"
  const [mountTime] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (pixels.length > 0) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [pixels.length]);
  const isSyncing = pixels.length === 0 && (pixelsLoading || now - mountTime < 5000);

  const pixelSelected = !!pixelId;
  const appSelected = !!appId;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Tracking</CardTitle>
        <CardDescription>
          Set your default pixel and app for conversion tracking. All fields are optional.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Pixel */}
        <div className="space-y-2">
          <Label>Default Dataset (Pixel)</Label>
          {isSyncing ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Syncing pixels...
            </div>
          ) : (
            <Combobox
              options={pixelOptions}
              value={pixelId}
              onValueChange={setPixelId}
              placeholder="Select a pixel..."
              searchPlaceholder="Search pixels..."
              emptyMessage="No pixels found."
            />
          )}
        </div>

        {/* App */}
        <div className="space-y-2">
          <Label>Default App</Label>
          <Combobox
            options={appOptions}
            value={appId}
            onValueChange={setAppId}
            placeholder="Select an app..."
            searchPlaceholder="Search apps..."
            emptyMessage="No apps found."
          />
        </div>

        {/* Conversion Event Sources */}
        <div className="space-y-1">
          <h4 className="text-sm font-medium text-foreground">Conversion Event Sources</h4>
          <div className="rounded-lg border border-border divide-y divide-border px-4">
            <LockedSwitch
              label="Website Events"
              checked={pixelSelected ? true : websiteEvents}
              locked={pixelSelected}
              lockReason="Required when Pixel is selected"
              onCheckedChange={(val) => setWebsiteEvents(val)}
            />
            <LockedSwitch
              label="App Events"
              checked={appSelected ? true : appEvents}
              locked={appSelected}
              lockReason="Required when App is selected"
              onCheckedChange={(val) => setAppEvents(val)}
            />
            <LockedSwitch
              label="Offline Events"
              checked={offlineEvents}
              locked={false}
              onCheckedChange={(val) => setOfflineEvents(val)}
            />
            <LockedSwitch
              label="CRM Events"
              checked={crmEvents}
              locked={false}
              onCheckedChange={(val) => setCrmEvents(val)}
            />
          </div>
        </div>

        {/* Engagement Event Sources */}
        <div className="space-y-1">
          <h4 className="text-sm font-medium text-foreground">Engagement Event Sources</h4>
          <div className="rounded-lg border border-border divide-y divide-border px-4">
            <LockedSwitch
              label="Post Engagements"
              checked={postEngagements}
              locked={false}
              onCheckedChange={(val) => setPostEngagements(val)}
            />
            <LockedSwitch
              label="Page Engagements"
              checked={pageEngagements}
              locked={false}
              onCheckedChange={(val) => setPageEngagements(val)}
            />
          </div>
        </div>

        {/* UTM Parameters */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Default UTM Parameters</label>
          <p className="text-xs text-muted-foreground">
            These UTM parameters will be appended to your ad URLs by default. Example: utm_source=facebook&amp;utm_medium=paid&amp;utm_campaign=summer_sale
          </p>
          <Textarea
            rows={2}
            placeholder="utm_source=facebook&utm_medium=paid&utm_campaign=summer_sale"
            value={utmParameters}
            onChange={(e) => setUtmParameters(e.target.value)}
          />
        </div>
      </CardContent>
    </Card>
  );
}
